import { Context, Deferred, Effect, Scope } from 'effect'

declare const browserResourceType: unique symbol

/** A replayable module input identifying one opaque value captured by the browser adapter. */
export type BrowserResourceRef<Resource> = {
  readonly id: string
  readonly [browserResourceType]?: Resource
}

export type BrowserResourceCapture = <Resource>(resource: Resource) => BrowserResourceRef<Resource>

export type BrowserResourceLease<Resource> = {
  readonly reference: BrowserResourceRef<Resource>
  /** Completes when another Effect program takes ownership of the resource. */
  readonly taken: Effect.Effect<void>
}

export type BrowserResourcesService = {
  /**
   * Transfers ownership of a captured browser value to one Effect program.
   * Reusing or inventing a reference is a defect: messages may identify physical
   * inputs, but only the browser boundary can introduce those inputs.
   */
  readonly take: <Resource>(reference: BrowserResourceRef<Resource>) => Effect.Effect<Resource>
  /**
   * Keeps an Effect-created browser value alive for exactly one consumer.
   * The enclosing Scope releases an unclaimed value; a successful `take`
   * completes the lease so its producer can finish normally.
   */
  readonly hold: <Resource>(
    resource: Resource
  ) => Effect.Effect<BrowserResourceLease<Resource>, never, Scope.Scope>
}

export class BrowserResources extends Context.Service<BrowserResources, BrowserResourcesService>()(
  'BrowserResources'
) {}

export type BrowserResourceRegistry = {
  readonly capture: BrowserResourceCapture
  readonly service: BrowserResourcesService
  readonly clear: () => void
}

/**
 * The one-cell physical boundary between React and the deterministic app.
 * Each store owns its registry, reference allocation, and cleanup so separate
 * simulations cannot observe or consume one another's browser inputs.
 */
export function makeBrowserResourceRegistry(): BrowserResourceRegistry {
  let nextId = 1
  const resources = new Map<
    string,
    { readonly value: unknown; readonly taken?: Deferred.Deferred<void> }
  >()

  function store<Resource>(
    resource: Resource,
    taken?: Deferred.Deferred<void>
  ): BrowserResourceRef<Resource> {
    const reference = { id: `browser-resource-${nextId}` }
    nextId += 1
    resources.set(reference.id, { value: resource, taken })
    return reference
  }

  function take<Resource>(reference: BrowserResourceRef<Resource>): Effect.Effect<Resource> {
    return Effect.suspend(() => {
      const entry = resources.get(reference.id)
      if (!entry) {
        return Effect.die(
          new Error(`Browser resource ${reference.id} is missing or was already consumed`)
        )
      }

      resources.delete(reference.id)
      const transferred = entry.taken
        ? Deferred.succeed(entry.taken, undefined).pipe(Effect.asVoid)
        : Effect.void
      return transferred.pipe(Effect.as(entry.value as Resource))
    })
  }

  function hold<Resource>(
    resource: Resource
  ): Effect.Effect<BrowserResourceLease<Resource>, never, Scope.Scope> {
    return Effect.gen(function* () {
      const taken = yield* Deferred.make<void>()
      const reference = yield* Effect.acquireRelease(
        Effect.sync(() => store(resource, taken)),
        (reference) =>
          Effect.sync(() => {
            resources.delete(reference.id)
          })
      )
      return { reference, taken: Deferred.await(taken) }
    })
  }

  return {
    capture: store,
    service: {
      take,
      hold
    },
    clear: () => resources.clear()
  }
}
